// Centralized configuration for the application
// API keys are loaded from environment variables - REQUIRED for security

import { logger } from './utils/logger';

/**
 * Validates that a required environment variable is present
 * Returns null if missing instead of throwing to allow graceful error handling
 */
const requiredEnvVar = (name: string): string | null => {
  const value = import.meta.env[name];
  if (!value) {
    const errorMessage = `Missing required environment variable: ${name}\n` +
      `Please set ${name} in your .env file. See env.example for reference.`;
    logger.error(errorMessage);
    return null;
  }
  return value;
};

/**
 * Optional environment variable with fallback
 * Only use for non-sensitive configuration values
 */
const optionalEnvVar = (name: string, fallback: string): string => {
  return import.meta.env[name] || fallback;
};

// Track configuration errors
let configError: Error | null = null;

/**
 * Validates all required Firebase environment variables
 * Returns null if any are missing, otherwise returns the config object
 */
const validateFirebaseConfig = (): { config: any; error: Error | null } => {
  const apiKey = requiredEnvVar('VITE_FIREBASE_API_KEY');
  const authDomain = requiredEnvVar('VITE_FIREBASE_AUTH_DOMAIN');
  const projectId = requiredEnvVar('VITE_FIREBASE_PROJECT_ID');
  const storageBucket = requiredEnvVar('VITE_FIREBASE_STORAGE_BUCKET');
  const messagingSenderId = requiredEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID');
  const appId = requiredEnvVar('VITE_FIREBASE_APP_ID');
  const measurementId = requiredEnvVar('VITE_FIREBASE_MEASUREMENT_ID');

  const missingVars: string[] = [];
  if (!apiKey) missingVars.push('VITE_FIREBASE_API_KEY');
  if (!authDomain) missingVars.push('VITE_FIREBASE_AUTH_DOMAIN');
  if (!projectId) missingVars.push('VITE_FIREBASE_PROJECT_ID');
  if (!storageBucket) missingVars.push('VITE_FIREBASE_STORAGE_BUCKET');
  if (!messagingSenderId) missingVars.push('VITE_FIREBASE_MESSAGING_SENDER_ID');
  if (!appId) missingVars.push('VITE_FIREBASE_APP_ID');
  if (!measurementId) missingVars.push('VITE_FIREBASE_MEASUREMENT_ID');

  if (missingVars.length > 0) {
    const error = new Error(
      `Missing required environment variables: ${missingVars.join(', ')}\n` +
      `Set these in .env for local builds, or in CI/CD secrets (e.g. GitHub Actions) for production. See env.example for reference.`
    );
    configError = error;
    return { config: null, error };
  }

  return {
    config: {
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId
    },
    error: null
  };
};

// Firebase Configuration - Validate and export
// This will be validated when firebase.ts imports it
const { config: firebaseConfigValue, error: firebaseConfigError } = validateFirebaseConfig();
if (firebaseConfigError) {
  configError = firebaseConfigError;
}

export const firebaseConfig = firebaseConfigValue || {
  apiKey: '',
  authDomain: '',
  projectId: '',
  storageBucket: '',
  messagingSenderId: '',
  appId: '',
  measurementId: ''
};

// Export error for checking
export const getConfigError = (): Error | null => configError;

// Google Maps / Gemini API Key - REQUIRED
export const getGoogleApiKey = (): string => {
  const key = requiredEnvVar('VITE_GOOGLE_API_KEY');
  if (!key) {
    const error = new Error(
      `Missing required environment variable: VITE_GOOGLE_API_KEY\n` +
      `Please set VITE_GOOGLE_API_KEY in your .env file. See env.example for reference.`
    );
    if (!configError) {
      configError = error;
    }
    throw error;
  }
  return key;
};

// App Configuration
export const appConfig = {
  appName: optionalEnvVar('VITE_APP_NAME', 'Rent Ready Pro'),
  version: optionalEnvVar('VITE_APP_VERSION', '0.0.0'),
  buildSha: optionalEnvVar('VITE_BUILD_SHA', ''),
  companyDomain: optionalEnvVar('VITE_COMPANY_DOMAIN', ''),
  isDevelopment: import.meta.env.VITE_USE_EMULATOR === 'true',
  /** Base URL for the app (e.g. https://app.pdxrentready.com). Used for email deep links. Falls back to window.location.origin in browser. */
  appBaseUrl: optionalEnvVar('VITE_APP_BASE_URL', ''),
  /** VAPID key for FCM web push (Firebase Console → Project settings → Cloud Messaging → Web Push certificates). Required for PWA push. */
  firebaseVapidKey: optionalEnvVar('VITE_FIREBASE_VAPID_KEY', ''),
  /** reCAPTCHA Enterprise ("Fraud Defense") site key for App Check. Public, not a secret. Blank = App Check off. See utils/appCheckSetup.ts. */
  recaptchaSiteKey: optionalEnvVar('VITE_RECAPTCHA_SITE_KEY', ''),
  /** Registered App Check debug token for localhost against real Firebase. Local only — never set in CI. */
  appCheckDebugToken: optionalEnvVar('VITE_APPCHECK_DEBUG_TOKEN', '')
};

